import React, { createContext, useContext, useState, useCallback, useMemo, useRef, useEffect as useEffectRaw } from 'react';
import { useAuth } from './AuthContext';
import { 
  patientAPI, 
  appointmentAPI, 
  ipdAPI, 
  laboratoryAPI, 
  radiologyAPI, 
  pharmacyAPI, 
  billingAPI, 
  proceduresAPI,
  inventoryAPI,
  maternalAPI
} from '../services/api';

const DataCacheContext = createContext();

export const useDataCache = () => {
  const context = useContext(DataCacheContext);
  if (!context) {
    throw new Error('useDataCache must be used within DataCacheProvider');
  }
  return context;
};

const CACHE_DURATION = 60 * 1000; // 60 seconds

// The patient list is cached under one key across the app. Prefetch and the
// Patients tab must request the exact same payload so the cache is never
// shared between two different response sizes (e.g. 100 vs 1000 rows).
export const PATIENT_LIST_KEY = 'patients:list';
const PATIENT_LIST_PARAMS = { limit: 1000 };

const BASE_CACHE_KEYS = [
  PATIENT_LIST_KEY,
  'patients',
  'appointments',
  'admissions',
  'wards',
  'beds',
  'labTests',
  'labOrders',
  'drugs',
  'prescriptions',
  'invoices',
  'radiologyStudies',
  'procedures',
  'inventory',
  'pregnancies',
  'children',
];

const buildInitialCache = (tenantId) => {
  const scoped = {};
  BASE_CACHE_KEYS.forEach(key => {
    scoped[`${tenantId}_${key}`] = { data: null, timestamp: null };
  });
  return scoped;
};

export const DataCacheProvider = ({ children }) => {
  const { user } = useAuth();
  const tenantId = user?.tenant_id || 'global';
  const tenantIdRef = useRef(tenantId);
  tenantIdRef.current = tenantId;

  // Memoized: this reads tenantIdRef.current (always current), so a stable
  // identity is safe. Without useCallback it is recreated every render, which
  // forces getCachedData (and every consumer callback built on it) into a new
  // identity → useEffect refires → refetch → updateCache → re-render → loop.
  // Visible as infinite "Loading..." flicker on legitimately-empty datasets,
  // since empty arrays are treated as invalid cache (see isCacheValid).
  const scopeKey = useCallback((key) => `${tenantIdRef.current}_${key}`, []);

  const [cache, setCache] = useState(() => buildInitialCache(tenantId));

  // Reset cache when tenant changes
  useEffectRaw(() => {
    setCache(buildInitialCache(tenantId));
  }, [tenantId]);

  // Listen for clearDataCache event (fired on logout)
  useEffectRaw(() => {
    const handler = () => setCache(buildInitialCache(tenantIdRef.current));
    window.addEventListener('clearDataCache', handler);
    return () => window.removeEventListener('clearDataCache', handler);
  }, []);

  const cacheRef = useRef(cache);
  cacheRef.current = cache;

  const isCacheValid = useCallback((baseKey) => {
    const cacheKey = scopeKey(baseKey);
    const cached = cacheRef.current[cacheKey];
    if (!cached || !cached.data || !cached.timestamp) return false;
    // Empty arrays/objects are not useful cache hits — treat them as invalid
    // so a fresh fetch is triggered instead of serving stale empty data (which
    // was the root cause of blank Billing tabs on SPA navigation: the
    // updateCache('invoices', []) effect on mount poisoned the cache with an
    // empty array that isCacheValid considered "valid" because [] is truthy).
    if (Array.isArray(cached.data) && cached.data.length === 0) return false;
    if (typeof cached.data === 'object' && !Array.isArray(cached.data) && Object.keys(cached.data).length === 0) return false;
    return Date.now() - cached.timestamp < CACHE_DURATION;
  }, [scopeKey]);

  const updateCache = useCallback((baseKey, data) => {
    const cacheKey = scopeKey(baseKey);
    const entry = { data, timestamp: Date.now() };
    // Sync the ref immediately: a getCachedData call in the same tick (before
    // React re-renders) reads cacheRef and must not see the stale entry.
    cacheRef.current = { ...cacheRef.current, [cacheKey]: entry };
    setCache(prev => ({
      ...prev,
      [cacheKey]: entry,
    }));
  }, [scopeKey]);

  const invalidateCache = useCallback((baseKey) => {
    const scopedBase = scopeKey(baseKey);
    // Apply to the ref synchronously — otherwise a refetch issued right after
    // invalidation (same tick, before React re-renders) sees the stale entry
    // and serves it as a cache hit (e.g. delete-then-refetch showing the
    // deleted row until a second attempt).
    const next = { ...cacheRef.current };
    for (const k of Object.keys(next)) {
      if (k === scopedBase || k.startsWith(`${scopedBase}:`)) delete next[k];
    }
    cacheRef.current = next;
    setCache(prev => {
      const out = {};
      for (const [k, v] of Object.entries(prev)) {
        if (k === scopedBase || k.startsWith(`${scopedBase}:`)) continue;
        out[k] = v;
      }
      return out;
    });
  }, [scopeKey]);

  // Track in-flight fetches so concurrent getCachedData calls for the same
  // key share a single network request instead of racing each other (e.g.
  // prefetchBilling and Billing.fetchData both calling getCachedData('invoices')
  // at the same time on SPA navigation).
  const pendingRequestsRef = useRef({});

  const getCachedData = useCallback(async (baseKey, fetchFunction, forceRefresh = false) => {
    const cacheKey = scopeKey(baseKey);
    if (!forceRefresh && isCacheValid(baseKey)) {
      return cacheRef.current[cacheKey].data;
    }

    // Deduplicate: if a fetch for this key is already in flight, wait for it
    // instead of starting a second concurrent request.
    if (pendingRequestsRef.current[cacheKey]) {
      return pendingRequestsRef.current[cacheKey];
    }

    const fetchPromise = (async () => {
      try {
        const response = await fetchFunction();
        const data = response?.data !== undefined ? response.data : response;
        updateCache(baseKey, data);
        return data;
      } catch (error) {
        console.error(`Error fetching ${baseKey}:`, error);
        if (cacheRef.current[cacheKey]?.data) {
          return cacheRef.current[cacheKey].data;
        }
        throw error;
      } finally {
        delete pendingRequestsRef.current[cacheKey];
      }
    })();

    pendingRequestsRef.current[cacheKey] = fetchPromise;
    return fetchPromise;
  }, [isCacheValid, updateCache, scopeKey]);

  // Prefetch functions for each module
  const prefetchPatients = useCallback(() => {
    if (!isCacheValid('patients')) {
      getCachedData(PATIENT_LIST_KEY, () => patientAPI.getAll(PATIENT_LIST_PARAMS)).catch(() => {});
    }
    if (!isCacheValid('appointments')) {
      getCachedData('appointments', () => appointmentAPI.getAll()).catch(() => {});
    }
  }, [getCachedData, isCacheValid]);

  const prefetchClinical = useCallback(() => {
    prefetchPatients();
    if (!isCacheValid('labOrders')) {
      getCachedData('labOrders', () => laboratoryAPI.getOrders({ limit: 10000 })).catch(() => {});
    }
  }, [getCachedData, isCacheValid, prefetchPatients]);

  const prefetchIPD = useCallback(() => {
    if (!isCacheValid('patients')) {
      getCachedData(PATIENT_LIST_KEY, () => patientAPI.getAll(PATIENT_LIST_PARAMS)).catch(() => {});
    }
    if (!isCacheValid('admissions')) {
      getCachedData('admissions', () => ipdAPI.getAdmissions()).catch(() => {});
    }
    if (!isCacheValid('wards')) {
      getCachedData('wards', () => ipdAPI.getWards()).catch(() => {});
    }
    if (!isCacheValid('beds')) {
      getCachedData('beds', () => ipdAPI.getBeds()).catch(() => {});
    }
  }, [getCachedData, isCacheValid]);

  const prefetchPharmacy = useCallback(() => {
    if (!isCacheValid('drugs')) {
      getCachedData('drugs', () => pharmacyAPI.getDrugs({})).catch(() => {});
    }
    if (!isCacheValid('prescriptions')) {
      getCachedData('prescriptions', () => pharmacyAPI.getPendingPrescriptions({})).catch(() => {});
    }
  }, [getCachedData, isCacheValid]);

  const prefetchLaboratory = useCallback(() => {
    if (!isCacheValid('labTests')) {
      getCachedData('labTests', () => laboratoryAPI.getTests({})).catch(() => {});
    }
    if (!isCacheValid('labOrders')) {
      getCachedData('labOrders', () => laboratoryAPI.getOrders({ limit: 10000 })).catch(() => {});
    }
  }, [getCachedData, isCacheValid]);

  const prefetchBilling = useCallback(() => {
    if (!isCacheValid('invoices')) {
      getCachedData('invoices', () => billingAPI.getInvoices({})).catch(() => {});
    }
    if (!isCacheValid('patients')) {
      getCachedData(PATIENT_LIST_KEY, () => patientAPI.getAll(PATIENT_LIST_PARAMS)).catch(() => {});
    }
  }, [getCachedData, isCacheValid]);

  const prefetchRadiology = useCallback(() => {
    if (!isCacheValid('radiologyStudies')) {
      getCachedData('radiologyStudies', () => radiologyAPI.getStudies({ limit: 200 })).catch(() => {});
    }
  }, [getCachedData, isCacheValid]);

  const prefetchProcedures = useCallback(() => {
    if (!isCacheValid('procedures')) {
      getCachedData('procedures', () => proceduresAPI.getOrders({})).catch(() => {});
    }
  }, [getCachedData, isCacheValid]);

  const prefetchInventory = useCallback(() => {
    if (!isCacheValid('inventory')) {
      getCachedData('inventory', () => inventoryAPI.getAllItems()).catch(() => {});
    }
  }, [getCachedData, isCacheValid]);

  const prefetchMaternal = useCallback(() => {
    if (!isCacheValid('pregnancies')) {
      getCachedData('pregnancies', () => maternalAPI.getPregnancies({ status: 'active' })).catch(() => {});
    }
    if (!isCacheValid('children')) {
      getCachedData('children', () => maternalAPI.getChildren()).catch(() => {});
    }
    if (!isCacheValid('patients')) {
      getCachedData(PATIENT_LIST_KEY, () => patientAPI.getAll(PATIENT_LIST_PARAMS)).catch(() => {});
    }
  }, [getCachedData, isCacheValid]);

  // Hydrate cache from preloaded data (used by InitialLoader).
  // Accepts unscoped keys and maps them to tenant-scoped keys internally.
  const hydrateFromPreload = useCallback((preloadedData) => {
    if (!preloadedData || typeof preloadedData !== 'object') return;
    setCache(prev => {
      const updates = {};
      Object.entries(preloadedData).forEach(([key, data]) => {
        const scopedKey = scopeKey(key);
        if (scopedKey in prev) {
          updates[scopedKey] = { data, timestamp: Date.now() };
        }
      });
      return { ...prev, ...updates };
    });
  }, [scopeKey]);

  // Build an unscoped view of the cache for external consumers that access cache.XXX directly
  const unscopedCache = useMemo(() => {
    const result = {};
    const prefix = `${tenantIdRef.current}_`;
    for (const [key, value] of Object.entries(cache)) {
      if (key.startsWith(prefix)) {
        result[key.slice(prefix.length)] = value;
      }
    }
    return result;
  }, [cache]);

  const value = useMemo(() => ({
    cache: unscopedCache,
    getCachedData,
    updateCache,
    invalidateCache,
    isCacheValid,
    // Prefetch functions
    prefetchPatients,
    prefetchClinical,
    prefetchIPD,
    prefetchPharmacy,
    prefetchLaboratory,
    prefetchBilling,
    prefetchRadiology,
    prefetchProcedures,
    prefetchInventory,
    prefetchMaternal,
    // Preload hydration
    hydrateFromPreload,
  }), [unscopedCache, getCachedData, updateCache, invalidateCache, isCacheValid, prefetchPatients, prefetchClinical, prefetchIPD, prefetchPharmacy, prefetchLaboratory, prefetchBilling, prefetchRadiology, prefetchProcedures, prefetchInventory, prefetchMaternal, hydrateFromPreload]);

  return (
    <DataCacheContext.Provider value={value}>
      {children}
    </DataCacheContext.Provider>
  );
};
